import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { getGame, DEFAULT_GAME } from "../lib/games";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import { Hash } from "lucide-react";

function LabelRow() {
  return (
    <div className="grid grid-cols-3">
      <div className="bg-orange-500 py-3 text-center text-white font-bold text-sm sm:text-base border-r border-white/40">Direct</div>
      <div className="bg-orange-500 py-3 text-center text-white font-bold text-sm sm:text-base border-r border-white/40">House</div>
      <div className="bg-orange-500 py-3 text-center text-white font-bold text-sm sm:text-base">Ending</div>
    </div>
  );
}

function ValueRow({ direct, house, ending, id, suffix }) {
  return (
    <div className="grid grid-cols-3">
      <div className="py-5 text-center border-r border-slate-200">
        <span data-testid={`common-direct${suffix}-${id}`} className="font-oswald text-2xl sm:text-3xl font-bold text-slate-900">{direct || "--"}</span>
      </div>
      <div className="py-5 text-center border-r border-slate-200">
        <span data-testid={`common-house${suffix}-${id}`} className="font-oswald text-2xl sm:text-3xl font-bold text-slate-900">{house || "--"}</span>
      </div>
      <div className="py-5 text-center">
        <span data-testid={`common-ending${suffix}-${id}`} className="font-oswald text-2xl sm:text-3xl font-bold text-slate-900">{ending || "--"}</span>
      </div>
    </div>
  );
}

function CommonCard({ c }) {
  const hasSet2 = c.direct_numbers2 || c.house2 || c.ending2;
  return (
    <div className="rounded-2xl overflow-hidden shadow-[0_6px_24px_rgb(0,0,0,0.10)] ring-1 ring-slate-200 bg-white" data-testid={`common-card-${c.id}`}>
      {/* Navy date header */}
      <div className="bg-slate-900 py-4 px-4 text-center">
        <span className="font-oswald text-xl sm:text-2xl font-bold tracking-wide text-white">Date: {c.date}</span>
      </div>

      {/* Set 1 */}
      <LabelRow />
      <ValueRow direct={c.direct_numbers} house={c.house} ending={c.ending} id={c.id} suffix="" />

      {/* Set 2 */}
      {hasSet2 ? (
        <div className="border-t border-slate-200">
          <LabelRow />
          <ValueRow direct={c.direct_numbers2} house={c.house2} ending={c.ending2} id={c.id} suffix="2" />
        </div>
      ) : null}

      {c.text ? (
        <p className="border-t border-slate-100 bg-slate-50 px-4 py-3 text-center text-sm text-slate-600">{c.text}</p>
      ) : null}
    </div>
  );
}

export default function CommonNumber() {
  const { game } = useParams();
  const slug = game || DEFAULT_GAME;
  const g = getGame(slug);
  const [item, setItem] = useState(null);

  useEffect(() => {
    let active = true;
    // Show cached value instantly (no loading), then refresh in background.
    try {
      const cached = sessionStorage.getItem(`cn_${slug}`);
      setItem(cached ? JSON.parse(cached) : null);
    } catch { setItem(null); }
    api.get(`/common-numbers/today?game=${slug}`).then(({ data }) => {
      if (!active) return;
      setItem(data);
      try { sessionStorage.setItem(`cn_${slug}`, JSON.stringify(data)); } catch {}
    });
    return () => { active = false; };
  }, [slug]);

  return (
    <Layout>
      <Seo
        title={`${g.name} Common Number Today | Direct House Ending`}
        description={`View today's ${g.name} Common Number including Direct, House and Ending numbers. Updated daily.`}
        path={game ? `/common-number/${slug}` : "/common-number"}
      />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="font-oswald text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Hash className="text-red-600" size={36} /> {g.name}
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Below is today's {g.name} Common Number, including Direct, House and Ending numbers. Provided
          for informational and entertainment purposes only.
        </p>

        <div className="mt-8 space-y-6" data-testid="common-number-list">
          {item ? (
            <CommonCard c={item} />
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 h-48 animate-pulse" />
          )}
        </div>
      </div>
    </Layout>
  );
}
